import { CancelledError } from "@tanstack/react-query"
import { createRootRouteWithContext, createRoute, Outlet, redirect } from "@tanstack/react-router"

import { createQueries, withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { NotAuthenticatedError } from "@/lib/oauth/AuthController.ts"
import { RootScene } from "@/routes/auth/RootScene.tsx"
import { Navigator } from "@/routes/Navigator.ts"

import type { GlobalContext, OrgContext } from "@/lib/context.ts"

export const RootRoute = createRootRouteWithContext<GlobalContext>()({
  component: () => <Outlet />,
})

export const ReservedRoute = createRoute({
  path: "~",
  component: () => <Outlet />,

  getParentRoute: () => RootRoute
})

const log = getLogger("OrgRoute")

export const OrgRoute = createRoute({
  path: "$orgId",
  beforeLoad: async ({ context, params }) => {
    if (!params.orgId) {
      throw new Error("Organization ID is required")
    }

    let orgContext = context.getOrgContext()
    if (orgContext == null || orgContext.orgId !== params.orgId) {
      try {
        await withGlobalLoader(context, context.authController.awaitReady())
        const queries = await withGlobalLoader(context, createQueries(params.orgId, context))
        orgContext = {
          ...context,
          orgId: params.orgId,
          orgToken: queries.apiClient.orgToken,
          queries: queries,
        } as OrgContext
        context.setOrgContext(orgContext)
      } catch (err) {
        if (err instanceof NotAuthenticatedError) {
          localStorage.setItem("redirect", location.toString())
          throw redirect(Navigator.login({ replace: true }))
        }

        log.error("awaitReady error", err)
        throw err
      }
    }

    return orgContext
  },

  onLeave: ({ context }) => {
    context.setOrgContext(null)
    try {
      void context.queryClient.clear()
    } catch (e) {
      if (e instanceof CancelledError) {
        // ignore
      } else {
        throw e
      }
    }
  },

  loader: async ({ context }) => {
    const principal = await context.queries.me()

    return { principal }
  },

  staleTime: Infinity,

  component: () => <RootScene />,
  getParentRoute: () => RootRoute
})

